import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { 
  Plus,
  Edit,
  Save
} from 'lucide-react';
import { resourcesAPI, availableDaysAPI } from '../services/api';
import toast from 'react-hot-toast';

const AvailableDays = () => {
  const [resources, setResources] = useState([]);
  const [availableDaysData, setAvailableDaysData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    month: '',
    resourceId: ''
  });
  const [availableMonths, setAvailableMonths] = useState([]);

  const [newEntry, setNewEntry] = useState({
    resource_id: '',
    month_year: '',
    total_days: 20,
    holidays: 0,
    sick_leave: 0,
    available_days: 20
  });

  // Generate last 12 months dynamically
  const generateLast12Months = () => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const monthName = date.toLocaleString('default', { month: 'long' });
      
      months.push({
        value: `${year}-${month}`,
        label: `${monthName} ${year}`
      });
    }
    
    return months;
  };

  // Get last month as default
  const getLastMonth = () => {
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const year = lastMonth.getFullYear();
    const month = String(lastMonth.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  useEffect(() => {
    // Initialize months and set default to last month
    const months = generateLast12Months();
    setAvailableMonths(months);
    
    if (!filters.month) {
      const lastMonth = getLastMonth();
      setFilters(prev => ({ ...prev, month: lastMonth }));
    }
    
    if (!newEntry.month_year) {
      const lastMonth = getLastMonth();
      setNewEntry(prev => ({ ...prev, month_year: lastMonth }));
    }
  }, []);

  useEffect(() => {
    if (filters.month) {
      loadResources();
      loadAvailableDaysData();
    }
  }, [filters]);

  // Auto-calculate available days when total days, holidays, or sick leave changes
  useEffect(() => {
    const available = Math.round((newEntry.total_days - newEntry.holidays - newEntry.sick_leave) * 100) / 100;
    setNewEntry(prev => ({ ...prev, available_days: Math.max(0, available) }));
  }, [newEntry.total_days, newEntry.holidays, newEntry.sick_leave]);

  const loadResources = async () => {
    try {
      const response = await resourcesAPI.getAll();
      setResources(response.data);
    } catch (error) {
      console.error('Error loading resources:', error);
      toast.error('Failed to load resources');
    }
  };

  const loadAvailableDaysData = async () => {
    try {
      setLoading(true);
      const response = await availableDaysAPI.getAll({
        month: filters.month,
        resourceId: filters.resourceId
      });
      setAvailableDaysData(response.data);
    } catch (error) {
      console.error('Error loading available days data:', error);
      toast.error('Failed to load available days data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newEntry.resource_id || !newEntry.month_year) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await availableDaysAPI.create({
        resourceId: newEntry.resource_id,
        monthYear: newEntry.month_year,
        totalWorkingDays: newEntry.total_days,
        holidays: newEntry.holidays,
        sickDays: newEntry.sick_leave,
        availableDays: newEntry.available_days,
        notes: ''
      });

      toast.success('Available days entry created successfully');
      setShowAddForm(false);
      setNewEntry({
        resource_id: '',
        month_year: '',
        total_days: 20,
        holidays: 0,
        sick_leave: 0,
        available_days: 20
      });
      loadAvailableDaysData();
    } catch (error) {
      console.error('Error creating available days entry:', error);
      toast.error('Failed to create available days entry');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setNewEntry({
      resource_id: item.resource_id,
      month_year: item.month_year,
      total_days: item.total_working_days,
      holidays: item.holidays,
      sick_leave: item.sick_days,
      available_days: item.available_days
    });
  };

  const handleUpdate = async () => {
    try {
      await availableDaysAPI.update(editingId, {
        resourceId: newEntry.resource_id,
        monthYear: newEntry.month_year,
        totalWorkingDays: newEntry.total_days,
        holidays: newEntry.holidays,
        sickDays: newEntry.sick_leave,
        availableDays: newEntry.available_days,
        notes: ''
      });

      toast.success('Available days entry updated successfully');
      setEditingId(null);
      setNewEntry({
        resource_id: '',
        month_year: '',
        total_days: 20,
        holidays: 0,
        sick_leave: 0,
        available_days: 20
      });
      loadAvailableDaysData();
    } catch (error) {
      console.error('Error updating available days:', error);
      toast.error('Failed to update available days');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    setNewEntry({
      resource_id: '',
      month_year: '',
      total_days: 20,
      holidays: 0,
      sick_leave: 0,
      available_days: 20
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading available days data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Available Days Management</h1>
          <p className="text-gray-600 mt-1">
            Manage resource availability by setting total days, holidays, and sick leave
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Entry
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <select
              value={filters.month}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Months</option>
              {availableMonths.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
            <select
              value={filters.resourceId}
              onChange={(e) => handleFilterChange('resourceId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Resources</option>
              {resources.map(resource => (
                <option key={resource.id} value={resource.id}>{resource.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Form */}
      {(showAddForm || editingId) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Available Days' : 'Add Available Days Entry'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resource *
                  </label>
                  <select
                    value={newEntry.resource_id}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, resource_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Resource</option>
                    {resources.map(resource => (
                      <option key={resource.id} value={resource.id}>{resource.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Month *
                  </label>
                  <select
                    value={newEntry.month_year}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, month_year: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Month</option>
                    {availableMonths.map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Days
                  </label>
                  <input
                    type="number"
                    value={newEntry.total_days}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, total_days: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="31"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Holidays
                  </label>
                  <input
                    type="number"
                    value={newEntry.holidays}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, holidays: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="31"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sick Leave
                  </label>
                  <input
                    type="number"
                    value={newEntry.sick_leave}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, sick_leave: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="31"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Available Days (Calculated)
                  </label>
                  <input
                    type="number"
                    value={newEntry.available_days}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    readOnly
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                {editingId ? (
                  <button
                    type="button"
                    onClick={handleUpdate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Update
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Available Days Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Available Days Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Days
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Holidays
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sick Leave
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Available Days
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {availableDaysData.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.resource_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.resource_type_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.month_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.total_working_days}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.holidays}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.sick_days}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.available_days}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {Math.round((item.available_days / item.total_working_days) * 100)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AvailableDays;
